// Staff tasks API (/staff-tasks). One endpoint family serves all three shells;
// the server derives the org from the caller's membership. installApiAuth()
// attaches the bearer to API-base URLs, so plain fetch is the pattern here.
import { apiUrl } from '../../lib/api'

export type TaskStatus = 'todo' | 'in_progress' | 'done'
export type TaskPriority = 'normal' | 'urgent'
export type TaskLinkKind = 'order' | 'product' | 'sample' | 'work_order'
export type TaskScope = 'mine' | 'created' | 'all'
/** Columns a header click sorts the task list on (server-side). */
export type TaskSort =
  | 'title'
  | 'assignee'
  | 'due_date'
  | 'status'
  | 'priority'
  | 'project'
  | 'stage'
  | 'created_by'
  | 'created_at'

export type TaskPerson = { user_id: string; display_name: string }
export type TaskLink = { kind: TaskLinkKind; id: string; label: string }
type ProjectRef = { id: string; name: string; is_private?: boolean }
export type RepeatRule = 'daily' | 'weekdays' | 'weekly' | 'monthly'
export const REPEAT_RULES: RepeatRule[] = ['daily', 'weekdays', 'weekly', 'monthly']
type MeetingNoteRef = { id: string; title: string; project_id: string }

export type StaffTask = {
  id: string
  title: string
  description: string | null
  /** First assignee (older readers); `assignees` holds them all. */
  assignee: TaskPerson | null
  assignees: TaskPerson[]
  created_by: TaskPerson | null
  due_date: string | null
  priority: TaskPriority
  status: TaskStatus
  link: TaskLink | null
  project?: ProjectRef | null
  stage_id?: string | null
  stage_name?: string | null
  repeat_rule?: RepeatRule | null
  repeat_until?: string | null
  meeting_note?: MeetingNoteRef | null
  is_overdue: boolean
  comment_count: number
  subtasks_done: number
  subtasks_total: number
  attachment_count: number
  can_delete: boolean
  completed_at: string | null
  created_at: string
  updated_at: string
}

export type TaskComment = {
  id: string
  author: TaskPerson | null
  body: string
  created_at: string
}

export type TaskActivity = {
  id: string
  actor: TaskPerson | null
  kind: string
  detail: Record<string, unknown>
  created_at: string
}

export type TaskSubtask = {
  id: string
  title: string
  is_done: boolean
  position: number
  completed_at: string | null
}

export type StaffTaskDetail = {
  task: StaffTask
  subtasks: TaskSubtask[]
  comments: TaskComment[]
  activity: TaskActivity[]
}

export type TaskListParams = {
  scope?: TaskScope
  status?: 'open' | TaskStatus
  assignee_user_id?: string
  overdue?: boolean
  linked_kind?: TaskLinkKind
  linked_id?: string
  q?: string
  due_from?: string
  due_to?: string
  project_id?: string
  sort?: TaskSort
  dir?: 'asc' | 'desc'
  limit?: number
  offset?: number
}

export type TaskListResponse = { items: StaffTask[]; total: number; limit: number; offset: number }
export type TaskSummary = { assigned_open: number; assigned_overdue: number }

export type TaskCreate = {
  title: string
  description?: string | null
  assignee_user_id?: string | null
  assignee_user_ids?: string[]
  due_date?: string | null
  priority?: TaskPriority
  link?: TaskLink | null
  project_id?: string | null
  stage_id?: string | null
  repeat_rule?: RepeatRule | null
  repeat_until?: string | null
}
export type TaskUpdate = Partial<Omit<TaskCreate, 'link'>> & { status?: TaskStatus }

export class StaffTaskApiError extends Error {
  status: number
  code: string | undefined
  constructor(status: number, code: string | undefined) {
    super(code ?? `HTTP ${status}`)
    this.status = status
    this.code = code
  }
}

async function call<T>(path: string, init?: RequestInit, base = '/staff-tasks'): Promise<T> {
  const res = await fetch(apiUrl(`${base}${path}`), {
    headers: { 'Content-Type': 'application/json' },
    ...init,
  })
  if (!res.ok) {
    const body = await res.json().catch(() => ({}))
    throw new StaffTaskApiError(res.status, body?.detail?.code)
  }
  return (res.status === 204 ? undefined : await res.json()) as T
}

function query(params: TaskListParams): string {
  const qs = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '' || value === false) continue
    qs.set(key, String(value))
  }
  const s = qs.toString()
  return s ? `?${s}` : ''
}

export const staffTaskKeys = {
  all: ['staff-tasks'] as const,
  list: (params: TaskListParams) => ['staff-tasks', 'list', params] as const,
  detail: (id: string) => ['staff-tasks', 'detail', id] as const,
  summary: ['staff-tasks', 'summary'] as const,
  notifications: ['staff-tasks', 'notifications'] as const,
  assignees: ['staff-tasks', 'assignees'] as const,
  projects: ['staff-tasks', 'projects'] as const,
  project: (id: string) => ['staff-tasks', 'projects', id] as const,
  templates: ['staff-tasks', 'project-templates'] as const,
  automations: ['staff-tasks', 'automations'] as const,
  folders: ['staff-tasks', 'folders'] as const,
  people: ['staff-tasks', 'people'] as const,
  notes: (projectId: string) => ['staff-tasks', 'projects', projectId, 'notes'] as const,
  note: (projectId: string, noteId: string) =>
    ['staff-tasks', 'projects', projectId, 'notes', noteId] as const,
}

export const listTasks = (params: TaskListParams) => call<TaskListResponse>(query(params))
export const getTask = (id: string) => call<StaffTaskDetail>(`/${id}`)
export const getTaskSummary = () => call<TaskSummary>('/summary')
export const listAssignees = () => call<{ items: TaskPerson[] }>('/assignees')
export const createTask = (body: TaskCreate) =>
  call<StaffTask>('', { method: 'POST', body: JSON.stringify(body) })
export const updateTask = (id: string, body: TaskUpdate) =>
  call<StaffTask>(`/${id}`, { method: 'PATCH', body: JSON.stringify(body) })
export const deleteTask = (id: string) => call<void>(`/${id}`, { method: 'DELETE' })
export const addSubtask = (id: string, title: string) =>
  call<TaskSubtask[]>(`/${id}/subtasks`, { method: 'POST', body: JSON.stringify({ title }) })
export const updateSubtask = (
  id: string,
  subtaskId: string,
  body: { title?: string; is_done?: boolean; position?: number },
) =>
  call<TaskSubtask[]>(`/${id}/subtasks/${subtaskId}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  })
export const deleteSubtask = (id: string, subtaskId: string) =>
  call<TaskSubtask[]>(`/${id}/subtasks/${subtaskId}`, { method: 'DELETE' })
export const addComment = (id: string, body: string) =>
  call<TaskComment>(`/${id}/comments`, { method: 'POST', body: JSON.stringify({ body }) })

export type TaskNotification = {
  id: string
  event_type: string
  title: string
  body: string | null
  source_entity_id: string | null
  is_read: boolean
  created_at: string
}
export type TaskNotificationList = { items: TaskNotification[]; unread_count: number }

export const listTaskNotifications = () => call<TaskNotificationList>('/notifications')
export const markTaskNotificationsRead = (ids?: string[]) =>
  call<{ updated: number }>('/notifications/read', {
    method: 'POST',
    body: JSON.stringify(ids ? { ids } : {}),
  })

// ---- Projects (/staff-projects, part 3) ----

export type ProjectStatus = 'on_track' | 'at_risk' | 'off_track' | 'on_hold' | 'done'
export const PROJECT_STATUSES: ProjectStatus[] = [
  'on_track',
  'at_risk',
  'off_track',
  'on_hold',
  'done',
]

export type StaffProject = {
  id: string
  name: string
  description: string | null
  owner: TaskPerson | null
  status: ProjectStatus
  start_date: string | null
  target_date: string | null
  tasks_done: number
  tasks_total: number
  tasks_overdue: number
  current_stage: string | null
  folder_id?: string | null
  /** Only `members` see it (and its tasks). */
  is_private?: boolean
  members?: TaskPerson[]
  /** The caller created it, so they choose privacy and members. */
  can_manage_members?: boolean
  can_delete: boolean
  created_at: string
  updated_at: string
}

export type ProjectStage = {
  id: string
  name: string
  position: number
  tasks_done: number
  tasks_total: number
}

export type StaffProjectDetail = { project: StaffProject; stages: ProjectStage[] }

export type ProjectCreate = {
  name: string
  description?: string | null
  owner_user_id?: string | null
  status?: ProjectStatus
  start_date?: string | null
  target_date?: string | null
  stages?: string[]
  template_id?: string | null
  folder_id?: string | null
  is_private?: boolean
  member_user_ids?: string[]
}
export type ProjectUpdate = Partial<Omit<ProjectCreate, 'stages' | 'template_id'>>

export type ProjectTemplate = {
  id: string
  name: string
  description: string | null
  stage_count: number
  task_count: number
  created_at: string
}

const P = '/staff-projects'
const json = (method: string, body: unknown): RequestInit => ({
  method,
  body: JSON.stringify(body),
})

export const listProjects = () => call<{ items: StaffProject[] }>('', undefined, P)
export const getProject = (id: string) => call<StaffProjectDetail>(`/${id}`, undefined, P)
export const createProject = (body: ProjectCreate) =>
  call<StaffProjectDetail>('', json('POST', body), P)
export const updateProject = (id: string, body: ProjectUpdate) =>
  call<StaffProjectDetail>(`/${id}`, json('PATCH', body), P)
export const deleteProject = (id: string) => call<void>(`/${id}`, { method: 'DELETE' }, P)
export const addStage = (id: string, name: string) =>
  call<StaffProjectDetail>(`/${id}/stages`, json('POST', { name }), P)
export const updateStage = (id: string, stageId: string, body: { name?: string; position?: number }) =>
  call<StaffProjectDetail>(`/${id}/stages/${stageId}`, json('PATCH', body), P)
export const deleteStage = (id: string, stageId: string) =>
  call<StaffProjectDetail>(`/${id}/stages/${stageId}`, { method: 'DELETE' }, P)
export const listTemplates = () => call<{ items: ProjectTemplate[] }>('/templates', undefined, P)
export const saveAsTemplate = (id: string, name: string) =>
  call<ProjectTemplate>(`/${id}/template`, json('POST', { name }), P)
export const deleteTemplate = (templateId: string) =>
  call<void>(`/templates/${templateId}`, { method: 'DELETE' }, P)

// ---- Automations (/staff-automations, part 4) ----

export type AutomationKind = 'done_notify' | 'overdue_notify' | 'project_assign'
export type AutomationTarget = 'opener' | 'project_owner' | 'user'

export type Automation = {
  id: string
  kind: AutomationKind
  project: ProjectRef | null
  stage_id: string | null
  stage_name: string | null
  target: AutomationTarget
  target_user: TaskPerson | null
  enabled: boolean
  run_count: number
  last_run_at: string | null
  created_at: string
}

export type AutomationCreate = {
  kind: AutomationKind
  project_id?: string | null
  stage_id?: string | null
  target: AutomationTarget
  target_user_id?: string | null
}

const A = '/staff-automations'
export const listAutomations = () => call<{ items: Automation[] }>('', undefined, A)
export const createAutomation = (body: AutomationCreate) =>
  call<Automation>('', json('POST', body), A)
export const setAutomationEnabled = (id: string, enabled: boolean) =>
  call<Automation>(`/${id}`, json('PATCH', { enabled }), A)
export const deleteAutomation = (id: string) => call<void>(`/${id}`, { method: 'DELETE' }, A)

// ---- Meeting notes (/staff-projects/{id}/notes, part 4) ----

type MeetingNoteItem = {
  id: string
  text: string
  assignee: TaskPerson | null
  due_date: string | null
  position: number
  task_id: string | null
}

export type MeetingNote = {
  id: string
  project_id: string
  title: string
  meeting_date: string | null
  body: string | null
  attendees: TaskPerson[]
  items: MeetingNoteItem[]
  created_by: TaskPerson | null
  can_delete: boolean
  created_at: string
  updated_at: string
}

export type MeetingNoteSummary = {
  id: string
  title: string
  meeting_date: string | null
  item_count: number
  open_item_count: number
  created_at: string
}

export type MeetingNoteItemInput = {
  text: string
  assignee_user_id?: string | null
  due_date?: string | null
}

export type MeetingNoteInput = {
  title: string
  meeting_date?: string | null
  body?: string | null
  attendee_user_ids?: string[]
}

const notesPath = (projectId: string) => `/${projectId}/notes`
export const listNotes = (projectId: string) =>
  call<{ items: MeetingNoteSummary[] }>(notesPath(projectId), undefined, P)
export const getNote = (projectId: string, noteId: string) =>
  call<MeetingNote>(`${notesPath(projectId)}/${noteId}`, undefined, P)
export const createNote = (
  projectId: string,
  body: MeetingNoteInput & { items?: MeetingNoteItemInput[] },
) => call<MeetingNote>(notesPath(projectId), json('POST', body), P)
export const updateNote = (projectId: string, noteId: string, body: Partial<MeetingNoteInput>) =>
  call<MeetingNote>(`${notesPath(projectId)}/${noteId}`, json('PATCH', body), P)
export const deleteNote = (projectId: string, noteId: string) =>
  call<void>(`${notesPath(projectId)}/${noteId}`, { method: 'DELETE' }, P)
export const addNoteItem = (projectId: string, noteId: string, body: MeetingNoteItemInput) =>
  call<MeetingNote>(`${notesPath(projectId)}/${noteId}/items`, json('POST', body), P)
export const deleteNoteItem = (projectId: string, noteId: string, itemId: string) =>
  call<MeetingNote>(`${notesPath(projectId)}/${noteId}/items/${itemId}`, { method: 'DELETE' }, P)
export const noteItemToTask = (projectId: string, noteId: string, itemId: string) =>
  call<StaffTask>(`${notesPath(projectId)}/${noteId}/items/${itemId}/task`, { method: 'POST' }, P)

// ---- Sidebar: folders and people (part 5) ----

export type ProjectFolder = { id: string; name: string; position: number; project_count: number }
export type PersonLoad = {
  user_id: string
  display_name: string
  open_count: number
  overdue_count: number
}

export const listFolders = () => call<{ items: ProjectFolder[] }>('/folders', undefined, P)
export const createFolder = (name: string) =>
  call<{ items: ProjectFolder[] }>('/folders', json('POST', { name }), P)
export const updateFolder = (id: string, body: { name?: string; position?: number }) =>
  call<{ items: ProjectFolder[] }>(`/folders/${id}`, json('PATCH', body), P)
export const deleteFolder = (id: string) =>
  call<{ items: ProjectFolder[] }>(`/folders/${id}`, { method: 'DELETE' }, P)
export const listPeople = () => call<{ items: PersonLoad[] }>('/people')
